import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const FOOTER_BG_COLOR = "#FAFAFA";

// Animation keyframes
const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const StyledFooter = styled("footer")(({ theme }) => ({
  padding: theme.spacing(6, 0, 3),
  backgroundColor: FOOTER_BG_COLOR,
  width: "100%",
  borderTop: "1px solid #EAEAEA",
  boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.05)",
  position: "relative",
  marginTop: theme.spacing(6),
  animation: `${fadeInUp} 1s ease-out`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-2px",
    left: 0,
    right: 0,
    height: "2px",
    background: "linear-gradient(90deg, #30B0C7 0%, #97D7E2 100%)",
  },
}));

const TaglineContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  padding: "2px",
  borderRadius: "4px",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.02)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: -10,
    width: "120%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(48, 176, 199, 0.1), transparent)",
    animation: `${shimmer} 2s infinite`,
  },
}));

const AnimatedTagline = styled(Typography)(({ theme }) => ({
  position: "relative",
  display: "inline-block",
  fontSize: "16px",
  fontWeight: 500,
  background: "linear-gradient(90deg, #30B0C7 0%, #97D7E2 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  lineHeight: 1.2,
  animation: `${fadeInUp} 0.8s ease-out`,
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: -2,
    width: "100%",
    height: "2px",
    background: "linear-gradient(90deg, #30B0C7 0%, #97D7E2 100%)",
    transform: "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.5s ease",
  },
  "&:hover": {
    animation: `${pulse} 1s ease infinite`,
    "&::after": {
      transform: "scaleX(1)",
    },
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: { xs: "center", md: "flex-start" },
  gap: theme.spacing(1.5),
  animation: `${fadeInUp} 0.8s ease-out`,
}));

const LogoImage = styled("img")(({ theme }) => ({
  width: "100%",
  maxWidth: "180px",
  height: "auto",
  transition: "all 0.3s ease",
  animation: `${float} 3s ease-in-out infinite`,
  "&:hover": {
    transform: "scale(1.05) translateY(-5px)",
    filter: "brightness(1.05)",
  },
}));

const SocialIconsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  justifyContent: "center",
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  animation: `${fadeInUp} 0.8s ease-out`,
  [theme.breakpoints.down("sm")]: {
    marginTop: theme.spacing(2),
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  border: "1px solid #e0e0e0",
  borderRadius: "12px",
  padding: theme.spacing(1.25),
  transition: "all 0.3s ease",
  backgroundColor: "#fff",
  animation: `${fadeInUp} 0.8s ease-out`,
  "&:hover": {
    transform: "translateY(-3px) scale(1.05)",
    boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
    backgroundColor: "rgba(48, 176, 199, 0.1)",
  },
  "& svg": {
    width: "32px",
    height: "32px",
    transition: "transform 0.3s ease",
  },
  "&:hover svg": {
    transform: "scale(1.1)",
  },
}));

const LinkContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2, 4),
  minHeight: { xs: "auto", md: "140px" },
  justifyContent: "center",
  animation: `${fadeInUp} 0.8s ease-out`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const FooterLink = styled(Typography)(({ theme }) => ({
  cursor: "pointer",
  fontSize: "0.95rem",
  position: "relative",
  paddingBottom: "2px",
  transition: "all 0.3s ease",
  textDecoration: "none",
  color: "inherit",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "0%",
    height: "2px",
    backgroundColor: theme.palette.primary.main,
    transition: "width 0.3s ease",
  },
  "&:hover": {
    color: theme.palette.primary.main,
    transform: "translateX(5px)",
    "&::after": {
      width: "100%",
    },
  },
}));

const FooterBottom = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: "1px solid #EAEAEA",
  textAlign: "center",
  color: theme.palette.text.secondary,
  animation: `${fadeInUp} 0.8s ease-out`,
  transition: "all 0.3s ease",
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

const Image23Container = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  animation: `${fadeInUp} 0.8s ease-out`,
  "& img": {
    width: "100%",
    maxWidth: "180px",
    height: "auto",
    transition: "all 0.3s ease",
    backgroundColor: FOOTER_BG_COLOR,
    mixBlendMode: "multiply",
    animation: `${float} 3s ease-in-out infinite`,
    "&:hover": {
      transform: "scale(1.05) translateY(-5px)",
      filter: "brightness(1.05)",
    },
  },
}));

export const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentYear = new Date().getFullYear();

  const linkGroups = [
    [
      { text: "Home", path: "/home" },
      { text: "About Us", path: "/aboutus" },
      // { text: "Dashboard", path: "/dashboard" },
      // { text: "Course", path: "/courses" },
    ],
    [
      { text: "Contact Us", path: "/contactus" },
      { text: "Careers", path: "/intern" },
      // { text: "IIITH_prep", path: "/iiithprep" },
    ],
  ];

  const socialLinks = [
    {
      Icon: TwitterIcon,
      color: "#1DA1F2",
      label: "Twitter",
      url: "https://twitter.com/PvtEd81554",
    },
    {
      Icon: InstagramIcon,
      color: "#E4405F",
      label: "Instagram",
      url: "https://www.instagram.com/passion_pravesh_?igsh=NXA3dHZpaGxlajdw",
    },
    {
      Icon: YouTubeIcon,
      color: "#FF0000",
      label: "YouTube",
      url: `https://youtube.com/@yapassio_official?si=p79dHix1iVoJR-3g`,
    },
    {
      Icon: LinkedInIcon,
      color: "#0A66C2",
      label: "LinkedIn",
      url: "https://www.linkedin.com/company/social-initiative-for-education-society/posts/?feedView=all",
    },
  ];

  const TaglineContent = () => (
    <TaglineContainer>
      <AnimatedTagline variant="h6">
        Crafting a career that suits you the most
      </AnimatedTagline>
    </TaglineContainer>
  );

  if (isMobile) {
    return (
      <StyledFooter>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid
                container
                justifyContent="center"
                sx={{
                  gap: { xs: 4, sm: 8 },
                  mb: 3,
                }}
              >
                {linkGroups.map((group, index) => (
                  <Box
                    key={index}
                    sx={{
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      backgroundColor: "rgba(151, 215, 226, 0.05)",
                      width: "auto",
                      minWidth: "140px",
                    }}
                  >
                    <LinkContainer>
                      {group.map((link, i) => (
                        <FooterLink
                          key={i}
                          component={Link}
                          to={link.path}
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 500,
                            textAlign: "center",
                          }}
                        >
                          {link.text}
                        </FooterLink>
                      ))}
                    </LinkContainer>
                  </Box>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ textAlign: "center", mb: 3 }}>
              <LogoImage src="logo.png" alt="Company Logo" />
            </Grid>

            <Grid item xs={12} sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ backgroundColor: FOOTER_BG_COLOR }}>
                <img
                  src="MSME.png"
                  alt="Additional Content"
                  style={{
                    maxWidth: "180px",
                    height: "auto",
                    mixBlendMode: "multiply",
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ textAlign: "center", mb: 3 }}>
              <TaglineContent />
            </Grid>
          </Grid>
        </Container>

        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            maxWidth: "600px",
            mb: 3,
          }}
        >
          <SocialIconsContainer>
            {socialLinks.map(({ Icon, color, label, url }) => (
              <StyledIconButton
                key={label}
                aria-label={label}
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ "& svg": { color } }}
              >
                <Icon />
              </StyledIconButton>
            ))}
          </SocialIconsContainer>
        </Container>

        <FooterBottom>
          <Typography variant="body2">
            © {currentYear} Yapassio. All rights reserved.
          </Typography>
        </FooterBottom>
      </StyledFooter>
    );
  }

  return (
    <StyledFooter>
      <Container maxWidth="lg">
        <Grid container spacing={0} alignItems="center" sx={{ mb: 4 }}>
          <Grid item md={3}>
            <LogoContainer>
              <LogoImage src="logo.png" alt="Company Logo" />
              <TaglineContent />
            </LogoContainer>
          </Grid>

          <Grid item md={6}>
            <Grid
              container
              justifyContent="center"
              sx={{
                gap: 18,
                mx: "auto",
                width: "fit-content",
              }}
            >
              {linkGroups.map((group, index) => (
                <Box
                  key={index}
                  sx={{
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(151, 215, 226, 0.05)",
                    },
                  }}
                >
                  <LinkContainer>
                    {group.map((link, i) => (
                      <FooterLink
                        key={i}
                        component={Link}
                        to={link.path}
                        sx={{
                          fontSize: "1rem",
                          fontWeight: 500,
                        }}
                      >
                        {link.text}
                      </FooterLink>
                    ))}
                  </LinkContainer>
                </Box>
              ))}
            </Grid>
          </Grid>

          <Grid item md={3}>
            <Image23Container>
              <img src="MSME.png" alt="Additional Content" />
            </Image23Container>
          </Grid>
        </Grid>

        <Grid container justifyContent="center">
          <Grid item md={6} sx={{ display: "flex", justifyContent: "center" }}>
            <SocialIconsContainer>
              {socialLinks.map(({ Icon, color, label, url }) => (
                <StyledIconButton
                  key={label}
                  aria-label={label}
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ "& svg": { color } }}
                >
                  <Icon />
                </StyledIconButton>
              ))}
            </SocialIconsContainer>
          </Grid>
        </Grid>

        <FooterBottom>
          <Typography variant="body2">
            © {currentYear} Yapassio. All rights reserved.
          </Typography>
        </FooterBottom>
      </Container>
    </StyledFooter>
  );
};

export default Footer;
